import type { TFunction } from 'i18next';

import type { DpiaDecision } from 'config/api/dpias/dpias.types';
import processing_activities_messages from 'messages/processing_activities_messages';

export type DpiaBadgeStyleClasses = {
  dpiaBadgeDanger: string;
  dpiaBadgeInfo: string;
  dpiaBadgeSuccess: string;
  dpiaBadgeWarn: string;
};

export type DpiaDisplayStatusSourceDto = {
  dpia_display_status?: string;
  dpia_indication_status?: string;
};

export type DpiaLabelContext = {
  dpiaRequestStatus?: string | null;
  dpiaLifecycleStatus?: string | null;
};

export const resolveDpiaDisplayStatus = (dto: DpiaDisplayStatusSourceDto): string => {
  if (typeof dto.dpia_display_status === 'string' && dto.dpia_display_status) {
    return dto.dpia_display_status;
  }

  if (dto.dpia_indication_status === 'not_indicated') {
    return 'not_indicated';
  }

  if (dto.dpia_indication_status === 'indicated') {
    return 'indicated';
  }

  return '';
};

export const getDpiaBadgeClass = (status: string, styles: DpiaBadgeStyleClasses) => {
  if (['indicated', 'required', 'dpia_required'].includes(status)) {
    return styles.dpiaBadgeDanger;
  }

  if (['ready_for_review', 'in_progress', 'clarification_requested', 'draft', 'draft_ao'].includes(status)) {
    return styles.dpiaBadgeWarn;
  }

  if (['decision_made', 'not_required', 'dpia_not_required', 'not_indicated', 'draft_ra'].includes(status)) {
    return styles.dpiaBadgeSuccess;
  }

  return styles.dpiaBadgeInfo;
};

export const getDpiaLabel = (status: string, t: TFunction) => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
  const message = messageMap[status];
  return message ? t(message) : status.replace(/_/g, ' ');
};

export const resolveApaDpiaDisplayLabel = (dpiaDisplayStatus: string, t: TFunction, context: DpiaLabelContext = {}): string => {
  if (context.dpiaRequestStatus === 'draft') {
    return t(processing_activities_messages.asset_ropa.dpia_status.draft_ra);
  }

  if (context.dpiaLifecycleStatus === 'draft') {
    return t(processing_activities_messages.asset_ropa.dpia_status.draft_ao);
  }

  return getDpiaLabel(dpiaDisplayStatus, t);
};

export const getDpiaDecisionLabel = (decision: DpiaDecision, t: TFunction) => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_decisions as Record<string, { label: string[] }>;
  const message = messageMap[decision]?.label;
  return message ? t(message) : decision.replace(/_/g, ' ');
};

export const getDpiaDecisionTooltip = (decision: DpiaDecision, t: TFunction) => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_decisions as Record<string, { tooltip: string[] }>;
  const message = messageMap[decision]?.tooltip;
  return message ? t(message) : '';
};
